import isValidDate from 'date-fns/isValid';
import { OPERATOR_GT_EQUALS, OPERATOR_LT_EQUALS } from '@atlaskit/jql-ast';
import type { IntlShape } from '@atlassian/jira-intl';
import type { JqlData } from '@atlassian/jira-jql-utils/src/utils/types.tsx';
import type { DateTimeValue, TypeConfig } from '../../common/types.tsx';
import messages from './messages.tsx';
import { IS_YYYY_MM_DD_REGEX } from './time-functions.tsx';

export const BETWEEN_CONFIG: TypeConfig = {
	type: 'between',
	componentType: 'between',
	label: 'labelBetween',
	hasInput: true,
	getErrorMessage: (intl: IntlShape, value?: DateTimeValue): string | undefined => {
		if (value?.value1 && value?.value2 && new Date(value?.value1) > new Date(value?.value2))
			return intl.formatMessage(messages.validationMessageBeforeGreaterThanAfter);
		if (
			value &&
			((value?.value1 !== '' && !isValidDate(new Date(value?.value1))) ||
				(value?.value2 !== '' && !isValidDate(new Date(value?.value2))))
		)
			return intl.formatMessage(messages.validationMessageInvalidDateFormat);
		return undefined;
	},
	getFormattedValue: (intl: IntlShape, value?: DateTimeValue) => {
		// Format Date based on 'UTC' because `new Date()` uses UTC when no timezone present, and JQL dates never have a timezone
		// JRACLOUD-79698: Without this, users west of UTC will see the dates off-by-one in the UI
		const formatOptions = { timeZone: 'UTC' };

		if (value) {
			if (value.value1 && value.value2) {
				if (isValidDate(new Date(value.value1)) && isValidDate(new Date(value.value2))) {
					return `${intl.formatDate(
						new Date(value.value1),
						formatOptions,
					)} - ${intl.formatDate(new Date(value.value2), formatOptions)}`;
				}
			} else if (
				(value.value1 && isValidDate(new Date(value.value1))) ||
				(value.value2 && isValidDate(new Date(value.value2)))
			) {
				const valueToformat = value.value1 || value.value2;
				return intl.formatDate(new Date(valueToformat), formatOptions);
			}
		}
		return '';
	},
	getJqlData: ({ value1, value2 }: Partial<DateTimeValue> = {}) => ({
		primaryClause: value1
			? {
					operator: OPERATOR_GT_EQUALS,
					operand: `${value1}`,
				}
			: undefined,
		secondaryClause: value2
			? {
					operator: OPERATOR_LT_EQUALS,
					operand: `${value2}`,
				}
			: undefined,
	}),
	getValueFromJqlData: (jqlData?: JqlData): DateTimeValue | undefined => {
		if (jqlData !== undefined) {
			const { primaryClause, secondaryClause } = jqlData;
			if (primaryClause === undefined && secondaryClause === undefined) {
				// Do not match type if both clauses are not present
				return undefined;
			}
			if (primaryClause !== undefined || secondaryClause !== undefined) {
				const returnValue = { value1: '', value2: '' };
				[primaryClause, secondaryClause].forEach((clause) => {
					if (clause) {
						const operatorValue = clause.operator ? clause.operator : '';
						const value = clause.operand !== undefined ? clause.operand : '';
						switch (operatorValue) {
							case OPERATOR_GT_EQUALS: {
								if (value.match(IS_YYYY_MM_DD_REGEX)) {
									returnValue.value1 = value;
								}
								break;
							}
							case OPERATOR_LT_EQUALS: {
								if (value.match(IS_YYYY_MM_DD_REGEX)) {
									returnValue.value2 = value;
								}
								break;
							}

							default:
								break;
						}
					}
				});
				if (returnValue.value1 || returnValue.value2) {
					return returnValue;
				}
			}
			return undefined;
		}
		return undefined;
	},
};
